import React, { useState, useEffect, lazy, Suspense } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import "./videoInterview.scss";
import VerificationPage from "./VerificationPage";
import { validateVerificationCode } from "../../Redux/ActionCreators/videoInterviewAction";

const VideoInterviewMain = lazy(() => import("./VideoInterviewMain"));

let isRequestingFullScreen = false;

const goFullScreen = () => {
  const element = document.documentElement;
  if (element.requestFullscreen) return element.requestFullscreen();
  if (element.mozRequestFullScreen) return element.mozRequestFullScreen();
  if (element.webkitRequestFullscreen) return element.webkitRequestFullscreen();
  if (element.msRequestFullscreen) return element.msRequestFullscreen();
  return Promise.reject("Fullscreen API is not supported.");
};

const attemptFullScreen = () => {
  if (isRequestingFullScreen || document.fullscreenElement) return;

  isRequestingFullScreen = true;
  goFullScreen()
    .catch((err) => {
      console.warn("Fullscreen error:", err.message);
    })
    .finally(() => {
      setTimeout(() => {
        isRequestingFullScreen = false;
      }, 1000);
    });
};

const VideoInterview = () => {
  const { token: videoCallId } = useParams();
  const [verificationCode, setVerificationCode] = useState("");
  const [step, setStep] = useState(-1);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const dispatch = useDispatch();
  const { connectionDetails, validating } = useSelector((state) => state.videoInterview);

  const handleVerification = async () => {
    attemptFullScreen();
    const result = await dispatch(validateVerificationCode(videoCallId, verificationCode));
    if (result.success) {
      attemptFullScreen(); // re-request fullscreen on success
      setStep(0); // this triggers main interview UI
    } else {
      setSnackbar({ open: true, message: result.message || "Invalid verification code", severity: "error" });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setSnackbar({ open: true, message: "You cannot exit full screen", severity: "error" });
        setTimeout(() => attemptFullScreen(), 100);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div>
      <div className="video-interview">
        {step === -1 ? (
          <VerificationPage
            code={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            onSubmit={handleVerification}
            validating={validating}
          />
        ) : (
          <Suspense fallback={<div>Loading interview...</div>}>
            <VideoInterviewMain connectionDetails={connectionDetails} />
          </Suspense>
        )}

        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
      <div className="video-interview-sm">
        <p>Video interview needs to be taken on Laptop or desktop.</p>
      </div>
    </div>
  );
};

export default VideoInterview;
